import React from 'react';

import Features from '@dashboard/enums/features';

import usePage from '@dashboard/hooks/usePage';

import CanUseFeature from '@dashboard/components/CanUseFeature';
import Link from '@dashboard/components/Link';

import PageEditView from '../components/PageEditView';

import EmailConfirmationForm from './PageSettings/components/EmailConfirmationForm';
import FunnelStepsForm from './PageSettings/components/FunnelStepsForm';
import PageConfirmationForm from './PageSettings/components/PageConfirmationForm';
import IntegrationsForm from './PageSettings/components/IntegrationsForm';
import CheckoutAbandonmentReminderEmailsForm from './PageSettings/components/CheckoutAbandonmentReminderEmailsForm';
import { PAGE_TYPES } from '@/dashboard/enums/page-types';

const PageAfterPayment = () => {
  const { page } = usePage();

  return (
    <PageEditView
      tabs={[
        {
          path: '/confirmation',
          label: page.type === 'form' ? 'After submission' : 'Payment confirmation',
          component: <PageConfirmationForm />,
          description:
            page.type === 'form'
              ? 'What your customer sees after submitting the form'
              : 'What your customer sees after successful payment',
        },
        {
          path: '/email-confirmation',
          label: 'Email confirmation',
          component: <EmailConfirmationForm />,
          description:
            page.type === 'form'
              ? 'What your customer receives after submitting the form'
              : 'What your customer receives after successful payment',
        },
        ...(page.type !== 'form'
          ? [
              {
                path: '/abandonment',
                label: 'Abandonment',
                title: 'Abandonment reminders',
                description: (
                  <>
                    Automatically send a sequence of 3 reminder emails when a checkout isn’t
                    completed.{' '}
                    <Link target="_blank" href="/help/articles/cart-abandonment">
                      Learn more
                    </Link>
                  </>
                ),
                component: <CheckoutAbandonmentReminderEmailsForm />,
              },
            ]
          : []),
        ...(page.type === 'checkout'
          ? [
              {
                path: '/one-click-upsell',
                label: 'One-click upsell',
                component: (
                  <CanUseFeature feature={Features.ONE_CLICK_UPSELLS}>
                    <FunnelStepsForm />
                  </CanUseFeature>
                ),
                description: (
                  <>
                    Show an additional one-click offer after successful payment.{' '}
                    <Link to="/help/articles/how-to-set-up-one-click-upsells" target="_blank">
                      Learn more
                    </Link>
                  </>
                ),
              },
            ]
          : []),
        {
          path: '/integrations',
          label: 'Integrations',
          description: (
            <>
              {page.type === PAGE_TYPES.CHECKOUT && (
                <div className="mb-4 text-gray-700">
                  Connect to your favorite apps and services. These integrations will be triggered
                  when a customer completes a purchase.{' '}
                  <Link href="/help/articles/integrations" target="_blank">
                    Learn more
                  </Link>
                </div>
              )}

              {page.type === PAGE_TYPES.EVENT && (
                <div className="mb-4 text-gray-700">
                  Connect to your favorite apps and services. These integrations will be triggered
                  when someone makes a booking.{' '}
                  <Link href="/help/articles/integrations" target="_blank">
                    Learn more
                  </Link>
                </div>
              )}

              {page.type === PAGE_TYPES.FORM && (
                <div className="mb-4 text-gray-700">
                  Connect to your favorite apps and services. These integrations will be triggered
                  when a customer submits a form.{' '}
                  <Link href="/help/articles/integrations" target="_blank">
                    Learn more
                  </Link>
                </div>
              )}
            </>
          ),
          component: <IntegrationsForm />,
        },
      ]}
    />
  );
};

export default PageAfterPayment;
